import { Banner, Section } from '../../shared/ui/layout';
import { Button } from '../../shared/ui/button';
import { Input } from '../../shared/ui/input';
import { Hint } from '../../shared/ui/hint';
import { Note } from '../../shared/ui/note';
import { OptionCard } from '../../shared/ui/option-card';
import { Checkbox } from '../../shared/ui/checkbox';
import { FieldLabel } from '../../shared/ui/field-label';
import { Segmented } from '../../shared/ui/segmented';
import { Select } from '../../shared/ui/select';
import { ApiError } from '../../shared/api/errors';
import { METHODS } from '../../entities/source';
import type { TSourceSettings } from './use-source-settings';

const CONTENT_TYPES = [
  'application/json',
  'text/plain',
  'application/xml',
  'text/xml',
  'text/html',
  'application/octet-stream',
] as const;

const AUTO = 'auto';

export const SourceName = ({ settings }: { settings: TSourceSettings }) => {
  const { draft, change } = settings;
  const refusal = settings.save.error instanceof ApiError ? settings.save.error : null;

  return (
    <>
      {settings.save.isError && (
        <Banner detail={refusal?.message ?? 'The source is still on its previous settings.'}>
          The settings were not accepted. Nothing was saved.
        </Banner>
      )}

      <Section title="What we call it">
        <Input
          className="max-w-form-narrow"
          aria-label="Name"
          value={draft.name}
          onChange={(event) => change({ name: event.target.value })}
        />
        {!settings.named && (
          <Note tone="danger" className="mt-3 max-w-form-narrow">
            A source needs a name. Saving is held until there is one.
          </Note>
        )}
      </Section>
    </>
  );
};

const FailureMode = ({
  settings,
  checked,
  onAddChecking,
}: {
  settings: TSourceSettings;
  checked: boolean;
  onAddChecking: () => void;
}) => {
  const { draft, change, source } = settings;

  if (!checked) {
    return (
      <Section
        title="When a check fails"
        description="Nothing is checked on this source, so nothing can fail — every request is taken as it came."
      >
        <div className="flex max-w-form-narrow items-center gap-inset rounded-control bg-surface-sunken px-input py-3">
          <span className="min-w-0 flex-1 text-meta text-muted">
            This choice only matters once a check exists.
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-control-xs shrink-0"
            onClick={onAddChecking}
          >
            Add checking first
          </Button>
        </div>
      </Section>
    );
  }

  return (
    <Section title="When a check fails">
      <div className="flex max-w-form-narrow flex-col gap-2">
        <OptionCard
          as="label"
          selected={draft.onAuthFailure === 'reject'}
          control={
            <input
              type="radio"
              className="accent-accent"
              name="on-auth-failure"
              checked={draft.onAuthFailure === 'reject'}
              onChange={() => change({ onAuthFailure: 'reject' })}
            />
          }
          title="Reject it"
          // The wire really answers 404, not the 401 the mockup drew; printing 401 would teach a false test.
          description={`Answer 404 — the same as an address that does not exist — and keep nothing. ${source.name} sees a failure and retries on its own schedule.`}
        />
        <OptionCard
          as="label"
          selected={draft.onAuthFailure === 'accept_and_flag'}
          control={
            <input
              type="radio"
              className="accent-accent"
              name="on-auth-failure"
              checked={draft.onAuthFailure === 'accept_and_flag'}
              onChange={() => change({ onAuthFailure: 'accept_and_flag' })}
            />
          }
          title="Keep it, deliver it, and mark it unverified"
          description="Store the request and deliver it like any other. Its events carry an Unverified mark, and the events list can filter on it. For the week you spend migrating a secret."
        />
      </div>
    </Section>
  );
};

export const SourceSettings = ({
  settings,
  checked,
  onAddChecking,
}: {
  settings: TSourceSettings;
  checked: boolean;
  onAddChecking: () => void;
}) => {
  const { draft, change, problems } = settings;

  const toggleMethod = (method: (typeof METHODS)[number], on: boolean) =>
    change({
      allowedMethods: on
        ? [...METHODS].filter((item) => item === method || draft.allowedMethods.includes(item))
        : draft.allowedMethods.filter((item) => item !== method),
    });

  const status = Number(draft.responseStatus);
  const customBody = draft.responseBody.trim().length > 0;

  return (
    <>
      <FailureMode settings={settings} checked={checked} onAddChecking={onAddChecking} />

      <Section
        title="Event type"
        description="Each platform puts it somewhere else, so this is set per source."
      >
        <div className="grid max-w-form-narrow grid-cols-2 gap-4">
          <div>
            <FieldLabel>Read the type from</FieldLabel>
            <Segmented
              aria-label="Read the type from"
              segments={[
                { value: 'body', label: 'the body' },
                { value: 'header', label: 'a header' },
              ]}
              value={draft.eventTypeFrom}
              onChange={(value) => change({ eventTypeFrom: value })}
            />
          </div>
          <div>
            <FieldLabel>Path</FieldLabel>
            <Input
              mono
              aria-label="Path"
              value={draft.eventTypePath}
              onChange={(event) => change({ eventTypePath: event.target.value })}
            />
          </div>
        </div>
      </Section>

      <Section
        id="customer"
        title="Customer"
        description="Whose request it is on the general address — what Events filters by Customer. A client’s own address always says whose it is."
      >
        <div className="flex max-w-form-narrow flex-col gap-4">
          <Segmented
            aria-label="Customer on the general address"
            segments={[
              { value: 'none', label: 'nobody' },
              { value: 'by_credential', label: 'the key that verified it' },
              { value: 'by_field', label: 'a field in the request' },
            ]}
            value={draft.customerResolutionMode}
            onChange={(value) => change({ customerResolutionMode: value })}
          />
          {draft.customerResolutionMode === 'by_field' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <FieldLabel>Read it from</FieldLabel>
                <Segmented
                  aria-label="Read the customer from"
                  segments={[
                    { value: 'body', label: 'the body' },
                    { value: 'header', label: 'a header' },
                    { value: 'query', label: 'the query' },
                  ]}
                  value={draft.customerFieldFrom}
                  onChange={(value) => change({ customerFieldFrom: value })}
                />
              </div>
              <div>
                <FieldLabel>Path</FieldLabel>
                <Input
                  mono
                  aria-label="Customer path"
                  placeholder={draft.customerFieldFrom === 'body' ? 'customer_id' : 'x-customer-id'}
                  invalid={problems.customerField}
                  value={draft.customerFieldPath}
                  onChange={(event) => change({ customerFieldPath: event.target.value })}
                />
              </div>
            </div>
          )}
          {problems.customerField && (
            <Note tone="danger">Say where the customer’s id is, or pick another way.</Note>
          )}
        </div>
      </Section>

      <Section title="What we accept">
        <div className="grid max-w-form-narrow grid-cols-2 gap-4">
          <div>
            <FieldLabel>Largest body accepted</FieldLabel>
            <Input
              aria-label="Largest body accepted"
              value={draft.maxBodyMb}
              inputMode="decimal"
              invalid={problems.body}
              trailing={<span className="text-meta text-faint">MB</span>}
              onChange={(event) => change({ maxBodyMb: event.target.value })}
            />
            <Hint>At most {settings.ceilingMb.toLocaleString('en-US')} MB on this deployment</Hint>
          </div>
          <div>
            <FieldLabel>Methods accepted</FieldLabel>
            <div className="flex h-control items-center gap-4">
              {METHODS.map((method) => (
                <Checkbox
                  key={method}
                  label={method}
                  checked={draft.allowedMethods.includes(method)}
                  onCheckedChange={(next) => toggleMethod(method, next)}
                />
              ))}
            </div>
          </div>
        </div>
        {problems.body && (
          <Note tone="danger" className="mt-3 max-w-form-narrow">
            The largest body has to be above 0 and at most{' '}
            {settings.ceilingMb.toLocaleString('en-US')} MB — the ingest process cuts every body off
            there, whatever a source says.
          </Note>
        )}
        {problems.methods && (
          <Note tone="danger" className="mt-3 max-w-form-narrow">
            Pick at least one method. With none, every request is answered 405 and nothing is kept.
          </Note>
        )}
      </Section>

      <Section title="What we answer">
        <div className="grid max-w-form-narrow grid-cols-2 gap-4">
          <div>
            <FieldLabel>Status we answer with</FieldLabel>
            <Input
              mono
              aria-label="Status we answer with"
              value={draft.responseStatus}
              inputMode="numeric"
              invalid={problems.status}
              onChange={(event) => change({ responseStatus: event.target.value })}
            />
          </div>
          <div>
            <FieldLabel>Content type</FieldLabel>
            <Select
              aria-label="Content type"
              value={draft.responseContentType || AUTO}
              onChange={(next) => change({ responseContentType: next === AUTO ? '' : next })}
              options={[
                {
                  value: AUTO,
                  label: settings.contentType ? `Automatic · ${settings.contentType}` : 'Automatic',
                },
                ...CONTENT_TYPES.map((type) => ({ value: type, label: type })),
              ]}
            />
          </div>
        </div>
        <div className="mt-4 max-w-form-narrow">
          <FieldLabel>Body we answer with</FieldLabel>
          <Input
            mono
            aria-label="Body we answer with"
            placeholder='{"requestId":"req_…"}'
            value={draft.responseBody}
            onChange={(event) => change({ responseBody: event.target.value })}
          />
          <Hint>
            {customBody
              ? 'This replaces the default reply entirely — the sender no longer gets the request id back.'
              : 'Empty: the sender gets {"requestId":"req_…"}, the id to look the request up by.'}
          </Hint>
        </div>
        {problems.status && (
          <Note tone="danger" className="mt-3 max-w-form-narrow">
            The status has to be a whole number from 200 to 599.
          </Note>
        )}
        {!problems.status && (status < 200 || status >= 300) && (
          <Note tone="danger" className="mt-3 max-w-form-narrow">
            {status} tells the sender its delivery failed, even though we kept the request. Most
            senders retry it — sending a duplicate — and many switch the webhook off after enough
            failures.
          </Note>
        )}
        {draft.onAuthFailure === 'accept_and_flag' && checked && (
          <Note className="mt-3 max-w-form-narrow">
            A request that fails its check and is kept anyway gets this same status.
            {customBody
              ? ' It gets this body too, so the sender cannot tell it was marked.'
              : ' The default reply adds "authFlagged": true to it.'}
          </Note>
        )}
      </Section>
    </>
  );
};
